import type { Country, MonetaryAmount } from '@backmarket/http-api'

import type {
  Address,
  BusinessDetails,
  IdentificationTypes,
  KybFile,
  Stakeholder,
  StakeholderInput,
  StakeholderRole,
} from '~/api/kyb/types'
import type {
  BranchAddress,
  BusinessFormValues,
} from '~/scopes/kyb/pages/components/steps/BusinessDetails/BusinessDetails.types'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'

export function transformStakeholderFormToApi(
  formValues: Omit<
    StakeholderFormValues,
    'photoIdFrontFile' | 'photoIdBackFile'
  > & { photoIdFront?: KybFile; photoIdBack?: KybFile },
  roles: StakeholderRole[],
  id?: string | null,
): StakeholderInput {
  const identityDocument = formValues.photoIdFront
    ? {
        identificationType: formValues.identificationType,
        countryOfIssue: formValues.countryOfIssue,
        front: formValues.photoIdFront,
        back: formValues.photoIdBack ?? null,
      }
    : null

  return {
    id: id ?? null,
    roles,
    firstName: formValues.firstName,
    kanaFirstName: formValues.kanaFirstName,
    latinFirstName: formValues.latinFirstName,
    lastName: formValues.lastName,
    kanaLastName: formValues.kanaLastName,
    latinLastName: formValues.latinLastName,
    dateOfBirth: formValues.dateOfBirth,
    countryOfBirth: formValues.countryOfBirth,
    countryOfCitizenship: formValues.countryOfCitizenship,
    phoneNumber: formValues.phoneNumber,
    address: {
      addressLine1: formValues.addressLine1,
      addressLine2: formValues.addressLine2 || null,
      postalCode: formValues.postalCode,
      city: formValues.city,
      stateOrProvince: formValues.stateOrProvince || null,
      country: formValues.country,
    },
    identityDocument,
  }
}

export function createStakeholderFormValues(
  stakeholder: Stakeholder | undefined,
  defaultCountry: Country,
): StakeholderFormValues {
  return {
    id: stakeholder?.id ?? null,
    firstName: stakeholder?.firstName ?? '',
    kanaFirstName: stakeholder?.kanaFirstName ?? '',
    latinFirstName: stakeholder?.latinFirstName ?? '',
    lastName: stakeholder?.lastName ?? '',
    kanaLastName: stakeholder?.kanaLastName ?? '',
    latinLastName: stakeholder?.latinLastName ?? '',
    dateOfBirth: stakeholder?.dateOfBirth ?? '',
    countryOfBirth: stakeholder?.countryOfBirth ?? defaultCountry,
    countryOfCitizenship: stakeholder?.countryOfCitizenship ?? defaultCountry,
    phoneNumber: stakeholder?.phoneNumber ?? '',
    addressLine1: stakeholder?.address?.addressLine1 ?? '',
    addressLine2: stakeholder?.address?.addressLine2 ?? '',
    postalCode: stakeholder?.address?.postalCode ?? '',
    city: stakeholder?.address?.city ?? '',
    country: (stakeholder?.address?.country as Country) ?? defaultCountry,
    identificationType:
      stakeholder?.identityDocument?.identificationType ??
      ('' as IdentificationTypes),
    countryOfIssue:
      stakeholder?.identityDocument?.countryOfIssue ?? ('' as Country),
    photoIdFrontFile: undefined,
    photoIdBackFile: undefined,
    stateOrProvince: stakeholder?.address.stateOrProvince || undefined,
  }
}

export function createEmptyStakeholderFormValues(
  defaultCountry: Country,
): StakeholderFormValues {
  return createStakeholderFormValues(undefined, defaultCountry)
}

export function createBusinessDetailsFormValuesFromApi(
  defaultCountry: Country,
  businessDetails?: BusinessDetails,
  branchesAddresses?: BranchAddress[],
): BusinessFormValues {
  return {
    legalName: businessDetails?.legalName ?? '',
    tradeName: businessDetails?.tradeName ?? '',
    kanaLegalName: businessDetails?.kanaLegalName ?? '',
    latinLegalName: businessDetails?.latinLegalName ?? '',
    email: businessDetails?.email ?? '',
    registrationCountry: businessDetails?.registrationCountry ?? defaultCountry,
    registrationType: businessDetails?.registrationType ?? '',
    registrationNumber: businessDetails?.registrationNumber ?? '',
    secondhandDealerNumber: businessDetails?.secondhandDealerNumber ?? '',
    secondhandDealerNumberIssuingPrefecture:
      businessDetails?.secondhandDealerNumberIssuingPrefecture ?? '',
    secondhandDealerLicence: businessDetails?.secondhandDealerLicence,
    incorporationDate: businessDetails?.incorporationDate ?? '',
    headquartersStreet:
      businessDetails?.headquartersAddress?.addressLine1 ?? '',
    headquartersStreet2:
      businessDetails?.headquartersAddress?.addressLine2 ?? '',
    headquartersCity: businessDetails?.headquartersAddress?.city ?? '',
    headquartersPostalCode:
      businessDetails?.headquartersAddress?.postalCode ?? '',
    headquartersCountry:
      businessDetails?.headquartersAddress?.country ?? defaultCountry,
    headquartersStateOrProvince:
      businessDetails?.headquartersAddress?.stateOrProvince ?? '',
    registeredBusinessStreet:
      businessDetails?.registeredBusinessAddress?.addressLine1 ?? '',
    registeredBusinessStreet2:
      businessDetails?.registeredBusinessAddress?.addressLine2 ?? '',
    registeredBusinessCity:
      businessDetails?.registeredBusinessAddress?.city ?? '',
    registeredBusinessPostalCode:
      businessDetails?.registeredBusinessAddress?.postalCode ?? '',
    registeredBusinessCountry:
      businessDetails?.registeredBusinessAddress?.country ?? defaultCountry,
    registeredBusinessStateOrProvince:
      businessDetails?.registeredBusinessAddress?.stateOrProvince ?? '',
    branchesAddresses: branchesAddresses || [],
    w9: businessDetails?.w9,
    articleOfIncorporation: businessDetails?.articleOfIncorporation,
    investedCapitalCurrency:
      businessDetails?.investedCapital?.currency ??
      ('' as MonetaryAmount['currency']),
    investedCapitalAmount: parseFloat(
      businessDetails?.investedCapital?.amount ?? '0',
    ),
  }
}

export function createBranchAddressFromApi(address: Address): BranchAddress {
  return {
    street: address.addressLine1,
    street2: address.addressLine2 ?? '',
    city: address.city,
    postalCode: address.postalCode,
    country: address.country || ('' as Country),
    stateOrProvince: address.stateOrProvince ?? '',
  }
}

export function transformBusinessDetailsFormToApi(
  formValues: BusinessFormValues,
): BusinessDetails {
  const { articleOfIncorporation } = formValues

  return {
    legalName: formValues.legalName,
    tradeName: formValues.tradeName,
    kanaLegalName: formValues.kanaLegalName || undefined,
    latinLegalName: formValues.latinLegalName || undefined,
    registrationCountry: formValues.registrationCountry,
    registrationType: formValues.registrationType,
    registrationNumber: formValues.registrationNumber,
    incorporationDate: formValues.incorporationDate,
    headquartersAddress: {
      addressLine1: formValues.headquartersStreet,
      addressLine2: formValues.headquartersStreet2 || null,
      city: formValues.headquartersCity,
      postalCode: formValues.headquartersPostalCode,
      country: formValues.headquartersCountry,
      stateOrProvince: formValues.headquartersStateOrProvince || null,
    },
    branchesAddresses: formValues.branchesAddresses.map((branch) => ({
      addressLine1: branch.street,
      addressLine2: branch.street2 || null,
      city: branch.city,
      postalCode: branch.postalCode,
      stateOrProvince: branch.stateOrProvince || null,
      country: branch.country as Country,
    })),
    registeredBusinessAddress: {
      addressLine1: formValues.registeredBusinessStreet,
      addressLine2: formValues.registeredBusinessStreet2 || null,
      city: formValues.registeredBusinessCity,
      postalCode: formValues.registeredBusinessPostalCode,
      stateOrProvince: formValues.registeredBusinessStateOrProvince || null,
      country: formValues.registeredBusinessCountry,
    },
    email: formValues.email,
    investedCapital: {
      currency: formValues.investedCapitalCurrency,
      amount: String(formValues.investedCapitalAmount || ''),
    },
    articleOfIncorporation,
    secondhandDealerNumber: formValues.secondhandDealerNumber || undefined,
    secondhandDealerNumberIssuingPrefecture:
      formValues.secondhandDealerNumberIssuingPrefecture || undefined,
    secondhandDealerLicence: formValues.secondhandDealerLicence || undefined,
    w9: formValues.w9,
  }
}
